/**
 * Completion content for the "finalize-care-plan-enrollment" task.
 * Renders ProgramSelectionForm and creates the operational care plan on submission.
 * @agentic connections
 */

import { Stack, Alert, Skeleton } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMedplum } from "@medplum/react";
import type {
  Patient,
  PractitionerRole,
  Observation,
} from "@medplum/fhirtypes";
import { ProgramSelectionForm } from "@/components/domain/care-plan/ProgramSelectionForm";
import { useCurrentUserOptional } from "@/app/auth/CurrentUserContext";
import { useProgramSelection } from "@/hooks/domain/useProgramSelection";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import type { ProgramSelectionFormData } from "@benchmark/core/types";
import {
  PROGRAM_SELECTION_OBSERVATION_SYSTEM,
  SELECTED_NEEDS_COMPONENT_CODE,
} from "@benchmark/core/constants";
import type {
  OperationalProgramCode,
  ProgramSelectionReasonCode,
} from "@benchmark/core/constants";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";
import { useTeeCareTimeForTask } from "@/hooks/domain/useTeeCareTimeForEncounter";
import type { CompletionContentProps } from "./DefaultCompletionContent";
import { getCarePlanIdFromTask } from "@benchmark/core/resolvers";

/**
 * Extracts ProgramSelectionFormData from an existing Observation.
 * @agentic connections
 */
const extractFormDataFromObservation = (
  obs: Observation,
): Partial<ProgramSelectionFormData> => {
  const selectedProgram =
    (obs.valueCodeableConcept?.coding?.[0]?.code as OperationalProgramCode) ??
    null;

  const reasonComponent = obs.component?.find(
    (c) =>
      c.code?.coding?.[0]?.system === PROGRAM_SELECTION_OBSERVATION_SYSTEM &&
      c.code?.coding?.[0]?.code === "selection-reason",
  );
  const reasonCodes = (reasonComponent?.valueString
    ?.split(",")
    .filter(Boolean) ?? []) as ProgramSelectionReasonCode[];

  const reasonText = obs.note?.[0]?.text ?? "";

  const needsComponent = obs.component?.find(
    (c) => c.code?.coding?.[0]?.code === SELECTED_NEEDS_COMPONENT_CODE.code,
  );
  const selectedNeeds =
    needsComponent?.valueString?.split(",").filter(Boolean) ?? [];

  const serviceNeeds = selectedNeeds.map((code) => ({
    id: crypto.randomUUID(),
    serviceType: code,
    modes: [] as string[],
    conditions: [] as string[],
    preferences: "",
  }));

  return {
    selectedProgram,
    reasonCodes,
    reasonText,
    selectedNeeds,
    serviceNeeds,
  };
};

/**
 * Completion content for the "finalize-care-plan-enrollment" activity.
 * Renders ProgramSelectionForm and creates operational care plan on submit.
 * @agentic connections
 */
export const FinalizeCarePlanEnrollmentCompletionContent = ({
  task,
  onComplete,
  onCancel,
  onDirtyChange,
  nextActivityLabel,
  nextActivityDefaultOwner,
  nextActivityAppliedRule,
  nextTaskOwnerOverride,
  onNextTaskOwnerOverrideChange,
  isResolvingNextTaskOwner = false,
}: CompletionContentProps) => {
  const steppedForm = useForm({
    initialValues: {
      placeholder: "",
    },
  });
  const medplum = useMedplum();
  // Prefer the shared current-user context (fetched once for the authenticated
  // tree). `useCurrentUserOptional` keeps a fallback inline fetch for the case
  // where this content renders outside `<CurrentUserProvider>` or before the
  // role has loaded, preserving the original behavior exactly.
  const currentUser = useCurrentUserOptional();
  const patientId = getResourceIdFromReference(task.for?.reference, "Patient");
  const carePlanId = getCarePlanIdFromTask(task);

  const {
    existingObservation,
    loading,
    submitting: hookSubmitting,
    error: hookError,
    gateValidation,
    submit,
  } = useProgramSelection(patientId, carePlanId);

  const [completionError, setCompletionError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [draftSelection, setDraftSelection] = useState<
    ProgramSelectionFormData | undefined
  >(undefined);
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);
  const teeForTask = useTeeCareTimeForTask();
  const steps = useMemo(
    () => [
      {
        id: "details",
        title: "Program Selection",
        subtitle: "Select operational program",
        validate: () =>
          !draftSelection
            ? { selectedProgram: "Select a valid operational program" }
            : null,
      },
      {
        id: "assignment",
        title: "Assignment",
        subtitle: "Review next task assignment",
      },
    ],
    [draftSelection],
  );

  const initialValues = useMemo(
    () =>
      existingObservation
        ? extractFormDataFromObservation(existingObservation)
        : undefined,
    [existingObservation],
  );

  const handleFormSubmit = useCallback(
    async (formData: ProgramSelectionFormData | undefined) => {
      if (!formData) return;
      if (!patientId || !carePlanId) {
        setCompletionError("Missing patient or care plan context");
        return;
      }

      setCompletionError(null);
      setCompleting(true);

      try {
        const includeReferralBatch =
          formData.selectedProgram === "triage" ||
          (formData.selectedProgram === "cocm" &&
            formData.includeReferrals === true);
        const includedServiceNeeds = includeReferralBatch
          ? (formData.serviceNeeds ?? [])
          : [];
        const normalizedFormData: ProgramSelectionFormData = {
          ...formData,
          serviceNeeds: includedServiceNeeds,
          selectedNeeds: includedServiceNeeds
            .map((need) => need.serviceType)
            .filter(Boolean),
          requiresApproval:
            includeReferralBatch && (formData.requiresApproval ?? true),
        };

        const patient = (await medplum.readResource(
          "Patient",
          patientId,
        )) as Patient;
        let practitionerRole = currentUser?.primaryPractitionerRole;
        if (!practitionerRole) {
          const practitionerRoles = await medplum.searchResources(
            "PractitionerRole",
            {
              practitioner: `Practitioner/${medplum.getProfile()?.id}`,
              _count: "1",
            },
          );
          practitionerRole = practitionerRoles[0] as
            PractitionerRole | undefined;
        }

        if (!practitionerRole) {
          throw new Error("No PractitionerRole found for current user");
        }

        const carePlan = await medplum.readResource("CarePlan", carePlanId);

        await submit(normalizedFormData, patient, practitionerRole, carePlan);

        await onComplete();
        await teeForTask({
          task,
          minutes: careMinutes,
          method: "task-completion",
        });
      } catch (err) {
        setCompletionError(
          err instanceof Error ? err.message : "Failed to complete enrollment",
        );
      } finally {
        setCompleting(false);
      }
    },
    [
      medplum,
      currentUser,
      patientId,
      carePlanId,
      submit,
      onComplete,
      teeForTask,
      task,
      careMinutes,
    ],
  );
  const handleSteppedSubmit = useCallback(async () => {
    await handleFormSubmit(draftSelection);
  }, [draftSelection, handleFormSubmit]);

  useEffect(() => {
    onDirtyChange?.(
      !!draftSelection || !!nextTaskOwnerOverride || !!careMinutes,
    );
  }, [draftSelection, nextTaskOwnerOverride, careMinutes, onDirtyChange]);

  if (!patientId || !carePlanId) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Unable to determine patient or care plan from this task.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Stack gap="sm">
        <Skeleton height={20} />
        <Skeleton height={120} />
        <Skeleton height={40} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {completionError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {completionError}
        </Alert>
      )}

      <SteppedForm
        form={steppedForm}
        steps={steps}
        onSubmit={handleSteppedSubmit}
        isSubmitting={hookSubmitting || completing}
        submitText="Complete Program Selection"
        onCancel={onCancel}
      >
        <FormStep id="details">
          <Stack gap="md">
            <ProgramSelectionForm
              onSubmit={setDraftSelection}
              submitting={hookSubmitting || completing}
              error={hookError}
              initialValues={initialValues}
              gateSatisfied={gateValidation?.isValid ?? false}
              allowEnrollLater={false}
              showReferralFields
            />
          </Stack>
        </FormStep>
        <FormStep id="assignment">
          <Stack gap="md">
            <NextTaskOwnerSection
              nextActivityLabel={nextActivityLabel}
              defaultOwner={nextActivityDefaultOwner}
              appliedRule={nextActivityAppliedRule}
              overrideOwner={nextTaskOwnerOverride}
              onOverrideChange={onNextTaskOwnerOverrideChange ?? (() => {})}
              isResolving={isResolvingNextTaskOwner}
            />
            <CareTimeActivityDurationField
              minutes={careMinutes}
              onMinutesChange={setCareMinutes}
            />
          </Stack>
        </FormStep>
      </SteppedForm>
    </Stack>
  );
};
